
import { z } from 'zod';

// Enhanced input validation schemas with cost optimization limits
export const personalizedFormSchema = z.object({
  voiceType: z.string().min(1, "Voice type is required").max(50, "Voice type too long"), // Reduced from 100
  voiceGender: z.enum(['male', 'female'], { required_error: "Voice gender is required" }),
  primaryGoal: z.string().min(1, "Primary goal is required").max(200, "Primary goal too long"), // Reduced from 500
  triggers: z.string().max(300, "Triggers description too long"), // Reduced from 1000
  desiredFeeling: z.string().min(1, "Desired feeling is required").max(100, "Desired feeling too long"), // Reduced from 200
  sessionLength: z.string().refine(val => ['3', '5', '10'].includes(val), "Invalid session length"), // Restricted to 3, 5, 10 minutes only
  additionalNotes: z.string().max(500, "Additional notes too long").optional(), // Reduced from 1000
  additionalInfo: z.string().max(500, "Additional info too long").optional()
});

export const biographySchema = z.object({
  biography: z.string()
    .min(10, "Biography must be at least 10 characters")
    .max(4000, "Biography must be less than 4000 characters") // Reduced from 5000
    .refine(text => !/<script|javascript:|data:/.test(text), "Invalid content detected")
});

export const sessionTitleSchema = z.string()
  .min(1, "Title is required")
  .max(50, "Title must be less than 50 characters") // Reduced from 100
  .refine(text => !/<script|javascript:|data:/.test(text), "Invalid content detected");

// PHASE 3: Ultra-optimized sample session limits for maximum cost savings
export const sampleSessionLimits = {
  maxDurationMinutes: 2,
  maxWords: 400, // Reduced from 700 (10-15% cost savings)
  maxCharacters: 2500 // Reduced from 4200 (10-15% cost savings)
};

// Custom session limits
export const customSessionLimits = {
  maxPromptLength: 500, // Characters for custom prompts
  maxDurationMinutes: 20
};

// Content validation for sample sessions
export const validateSampleContent = (content: string): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];
  const wordCount = content.trim().split(/\s+/).length;
  
  if (wordCount > sampleSessionLimits.maxWords) {
    errors.push(`Sample content exceeds ${sampleSessionLimits.maxWords} words (current: ${wordCount})`);
  }
  
  if (content.length > sampleSessionLimits.maxCharacters) {
    errors.push(`Sample content exceeds ${sampleSessionLimits.maxCharacters} characters`);
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

// Sanitization functions with optimization
export const sanitizeInput = (input: string): string => {
  return input
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/javascript:/gi, '')
    .replace(/data:/gi, '')
    .replace(/on\w+\s*=/gi, '')
    .trim();
};

export const sanitizeHtml = (html: string): string => {
  // Remove all HTML tags except basic formatting
  return html.replace(/<(?!\/?(?:b|i|em|strong|br)\b)[^>]*>/gi, '');
};

// Optimized biography processing - extract only key insights
export const optimizeBiographyForCost = (biography: string): string => {
  if (!biography || biography.length <= 1000) return biography;
  
  // Extract key information patterns
  const keyPatterns = [
    /name[s]?\s*(?:is|are|:)?\s*([^.!?]+)/gi,
    /work[s]?\s*(?:as|at|in)?\s*([^.!?]+)/gi,
    /live[s]?\s*(?:in|at)?\s*([^.!?]+)/gi,
    /family[^.!?]+/gi,
    /pet[s]?[^.!?]+/gi,
    /hobby[^.!?]+/gi,
    /hobies[^.!?]+/gi,
    /interest[s]?[^.!?]+/gi,
    /goal[s]?[^.!?]+/gi,
    /challenge[s]?[^.!?]+/gi,
    /stress[^.!?]+/gi
  ];
  
  const extractedInfo: string[] = [];
  
  keyPatterns.forEach(pattern => {
    const matches = biography.match(pattern);
    if (matches) {
      extractedInfo.push(...matches.slice(0, 2)); // Limit matches per pattern
    }
  });
  
  let optimized = extractedInfo.join('. ').substring(0, 1000);
  if (optimized.length < biography.length) {
    optimized += '...';
  }
  
  return optimized || biography.substring(0, 1000);
};

// Rate limiting configurations with tighter limits
export const RATE_LIMITS = {
  AUDIO_GENERATION: { action: 'audio_generation', maxRequests: 8, windowMinutes: 60 }, // Reduced from 10
  CONTENT_GENERATION: { action: 'content_generation', maxRequests: 15, windowMinutes: 60 }, // Reduced from 20
  PROFILE_UPDATE: { action: 'profile_update', maxRequests: 5, windowMinutes: 15 },
  SESSION_SAVE: { action: 'session_save', maxRequests: 50, windowMinutes: 60 }
} as const;
